import { useEffect, useMemo, useRef } from "react";
import { useCart } from "../Contexts/CartContext";
import { useCartListings } from "./useCartListings";
import { showToast } from "../utils/toast"; // match actual showToast location

const flattenIds = (cartItems) =>
  Object.values(cartItems || {})
    .flat()
    .map((it) => it.listing_id)
    .filter((id) => id != null);

export const useCartWithListings = () => {
  const { cartItems, setCartItems } = useCart();
  const ids = useMemo(() => flattenIds(cartItems), [cartItems]);
  const { listingsById, isLoading, isError, refetch } = useCartListings(ids);
  const reconciledRef = useRef("");

  // Build the merged, fresh {card_listing, quantity} structure for rendering.
  const cartData = useMemo(() => {
    const out = {};
    for (const [sellerId, items] of Object.entries(cartItems || {})) {
      const merged = [];
      for (const it of items) {
        const listing = listingsById[it.listing_id];
        if (!listing) continue; // unavailable: excluded from render (pruned below)
        const stock = listing.quantity ?? 0;
        merged.push({
          card_listing: listing,
          quantity: Math.min(it.quantity, stock),
        });
      }
      if (merged.length > 0) out[sellerId] = merged;
    }
    return out;
  }, [cartItems, listingsById]);

  // Reconcile persisted cart against fresh data: prune unavailable, clamp stock.
  // Only runs once listings have loaded and only when something actually changes.
  useEffect(() => {
    if (isLoading || isError || ids.length === 0) return;

    let removed = false;
    let clamped = false;
    let clampedStock = null;
    const next = {};
    for (const [sellerId, items] of Object.entries(cartItems || {})) {
      const kept = [];
      for (const it of items) {
        const listing = listingsById[it.listing_id];
        if (!listing) {
          removed = true;
          continue;
        }
        const stock = listing.quantity ?? 0;
        const qty = Math.min(it.quantity, stock);
        if (qty <= 0) {
          removed = true;
          continue;
        }
        if (qty !== it.quantity) {
          clamped = true;
          clampedStock = stock;
        }
        kept.push({ listing_id: it.listing_id, quantity: qty });
      }
      if (kept.length > 0) next[sellerId] = kept;
    }

    if (!removed && !clamped) return;

    // Guard against re-running for the same reconciliation result.
    const signature = JSON.stringify(next);
    if (reconciledRef.current === signature) return;
    reconciledRef.current = signature;

    setCartItems(next);
    if (removed) {
      showToast.error("Some items are no longer available and were removed from your cart.");
    } else if (clamped) {
      showToast.info(`Only ${clampedStock} left; quantity updated.`);
    }
  }, [cartItems, listingsById, isLoading, isError, ids.length, setCartItems]);

  return { cartData, isLoading, isError, refetch };
};
